/**
 * Small, pure parsers that turn stored text into study facts, or into
 * `null` when the text does not say. Every parser here prefers "unknown" to
 * a guess: unrecognised text is null, never the nearest match.
 */
import { cleanProtocolField } from "../utils/protocolFields";
import type { Blinding } from "./types";
import { negationAt } from "./negation";

/** A trimmed string, or null for empty and placeholder words ("unknown", "Not specified"). */
export function text(v: unknown): string | null {
  const s = cleanProtocolField(v);
  return s ? s : null;
}

/** yes/no/unknown (or a real boolean) -> boolean | null. */
export function triState(v: unknown): boolean | null {
  if (v === true || v === "yes") return true;
  if (v === false || v === "no") return false;
  return null;
}

/** One of `allowed`, or null (covers 'unknown', empty and any stray value). */
export function oneOf<T extends string>(v: unknown, allowed: readonly T[]): T | null {
  return typeof v === "string" && (allowed as readonly string[]).includes(v) ? (v as T) : null;
}

const L = "(?<![\\p{L}\\p{N}])";
const R = "(?![\\p{L}\\p{N}])";
const re = (body: string) => new RegExp(`${L}(?:${body})${R}`, "iu");

/** Phrases that say the design is NOT blinded. Checked before any "blind" word. */
const OPEN_LABEL = re(
  "open[-\\s]?label|non[-\\s]?blind(?:ed)?|un[-\\s]?blind(?:ed)?|not\\s+blinded|no\\s+blinding|un[-\\s]?masked|non[-\\s]?masked",
);
const TRIPLE = re("triple[-\\s]?(?:blind(?:ed)?|mask(?:ed)?)");
const DOUBLE = re("double[-\\s]?(?:blind(?:ed)?|mask(?:ed)?)");
const SINGLE = re("single[-\\s]?(?:blind(?:ed)?|mask(?:ed)?)");
/** Blinded, level not stated ("blinded", "masked", "investigator-blinded"). */
const ANY_BLIND = re("[\\p{L}-]*blind(?:ed|ing)?|masked|masking");

export interface BlindingReading {
  /** The level, when the text states exactly one. */
  level: Blinding | null;
  /** Whether TREATMENT assignment is masked, when the text says. */
  blinded: boolean | null;
  /**
   * Assessor- or reviewer-level blinding the text mentions (e.g. a blinded
   * independent central review of scans). Recorded, but it is not masking of
   * treatment assignment and never sets the blinding feature.
   */
  note?: string;
}

/**
 * Blinding of an ASSESSMENT rather than of treatment assignment: BICR/BIRC,
 * "blinded independent central review", "assessor-blinded", "blinded central
 * read". An open-label study commonly has one; it stays open-label.
 */
const ASSESSOR_BLINDING = new RegExp(
  `${L}(?:bicr|birc|blinded\\s+independent\\s+(?:central\\s+)?(?:radiology\\s+|imaging\\s+)?review(?:\\s+committee)?|independent\\s+blinded\\s+(?:central\\s+)?review|(?:assessor|evaluator|rater|reader|reviewer|outcome[-\\s]assessor|endpoint(?:[-\\s]assessor)?)[-\\s](?:blind(?:ed)?|mask(?:ed)?)|blind(?:ed)?\\s+(?:independent\\s+)?(?:central\\s+)?(?:read(?:er|ing)?|review(?:er)?|assessment|evaluation|adjudication)(?:\\s+committee)?)${R}`,
  "giu",
);

/**
 * Read a protocol's blinding text. "Open-label", "non-blinded", "unblinded"
 * and a bare "none" are open-label — "non-blinded" CONTAINS "blind", which
 * is the bug drcpGenerator had, so negations are removed first. Assessor-
 * level blinding (BICR and the like) is removed next and kept as a `note`:
 * "Open-label; blinded independent central review" is open-label. Text
 * naming two TREATMENT levels ("open-label Part A, double-blind Part B")
 * gives no single level but is still blinded.
 */
export function parseBlinding(v: unknown): BlindingReading {
  const t = text(v);
  if (!t) return { level: null, blinded: null };
  if (/^(none|open|no)$/i.test(t)) return { level: "open_label", blinded: false };

  const assessorHits = [...t.matchAll(ASSESSOR_BLINDING)].map((m) => m[0]);
  const note = assessorHits.length
    ? `Assessment-level blinding only, not masking of treatment: ${[...new Set(assessorHits)].join("; ")}`
    : undefined;
  const withNote = (r: BlindingReading): BlindingReading => (note ? { ...r, note } : r);

  const noAssessor = t.replace(ASSESSOR_BLINDING, " ");
  // Each open-label token is judged where it stands. A NEGATED one ("not
  // open-label") is not open-label evidence. A PROCEDURAL "unblinded" ("no
  // patient will be unblinded except in a medical emergency") is a rule for
  // breaking the blind, so it is evidence the study IS blinded.
  let open = false;
  let procedural = false;
  for (const m of noAssessor.matchAll(new RegExp(OPEN_LABEL.source, "giu"))) {
    const idx = m.index ?? 0;
    const len = m[0].length;
    if (/^un[-\s]?blind/i.test(m[0]) && isProceduralUnblinding(noAssessor, idx, len)) {
      procedural = true;
      continue;
    }
    if (negationAt(noAssessor, idx, len) === "negated") continue;
    open = true;
  }
  const rest = noAssessor.replace(new RegExp(OPEN_LABEL.source, "giu"), " ");
  const levels: Blinding[] = [];
  if (TRIPLE.test(rest)) levels.push("triple_blind");
  if (DOUBLE.test(rest)) levels.push("double_blind");
  if (SINGLE.test(rest)) levels.push("single_blind");
  const blindedOther = ANY_BLIND.test(rest) || procedural;

  if (open && levels.length === 0 && !blindedOther) return withNote({ level: "open_label", blinded: false });
  if (!open && levels.length === 1) return withNote({ level: levels[0], blinded: true });
  if (levels.length > 0 || blindedOther) return withNote({ level: null, blinded: true });
  return withNote({ level: null, blinded: null });
}

/**
 * "unblinded" used as a VERB about breaking the blind — "will be unblinded",
 * "unblinded except in an emergency", "unblinded only if" — rather than as a
 * description of the design ("an unblinded study").
 */
function isProceduralUnblinding(t: string, idx: number, len: number): boolean {
  const before = t.slice(0, idx);
  const after = t.slice(idx + len);
  if (/(?:^|[^\p{L}\p{N}])(?:be|been|being|is|are|was|were|get|gets|got|remain|remains)\s+$/iu.test(before)) return true;
  return /^\s*,?\s*(?:except|only|if|when|unless|in\s+(?:case|the\s+event)|for\s+(?:safety|emergenc\p{L}*)|during|after|at\s+the\s+end)(?![\p{L}\p{N}])/iu.test(after);
}

/** Every randomization word, including an attached "non"/"un" prefix. */
const RANDOM_WORD = new RegExp(`${L}(?:non|un)?-?randomi[sz]\\p{L}*`, "giu");
const SINGLE_ARM = new RegExp(`${L}single[-\\s]arm${R}`, "iu");

/**
 * Read a protocol's randomization text. Each randomization word is judged
 * in its own clause by `negationAt`, so negation works in either order —
 * "not randomized", "no randomisation", "randomisation will not be
 * performed", "Randomization: none / N/A / not applicable" — while a
 * negation in another clause ("Randomized; not blinded") does not count.
 * Any un-negated mention makes the answer true ("randomized after a
 * non-randomized lead-in"); only negated mentions, or "single-arm" with no
 * mention, make it false; text with neither is null.
 */
export function parseRandomized(v: unknown): boolean | null {
  if (typeof v === "boolean") return v;
  const t = text(v);
  if (!t) return null;
  if (/^(none|no|n\/?a|not\s+applicable)$/i.test(t)) return false;
  let positive = false;
  let negative = false;
  let subgroup = false;
  for (const m of t.matchAll(RANDOM_WORD)) {
    const attached = /^(?:non|un)-?/i.test(m[0]);
    const kind = attached ? "negated" : negationAt(t, m.index ?? 0, m[0].length);
    if (kind === "negated") negative = true;
    else if (kind === "subgroup") subgroup = true;
    else positive = true;
  }
  if (positive) return true;
  // A "not" about some participants ("subjects who are not randomized within
  // 28 days", "participants not eligible for randomization") suggests the
  // study randomizes others but does not say so outright: mixed evidence is
  // TBD, never a confident "no".
  if (subgroup) return null;
  if (negative || SINGLE_ARM.test(t)) return false;
  return null;
}

/** A positive whole number, or null. */
export function positiveNumber(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) && v > 0 ? v : null;
}
